import React, { useState, useEffect, useRef } from 'react';
import { BookOpen, Target, Clock, Award, ChevronDown, ChevronUp, CheckCircle, AlertCircle, Play, ChevronRight, Calculator, Brain, Download, Zap } from 'lucide-react';
import { hkdseMathsData } from '../data/hkdseMathsData.js';
import PastPaperViewer from './PastPaperViewer';
import GeogebraGraph from './GeogebraGraph';
import Assessment from './Assessment';
import M01NumberSystemsExercise from './M01NumberSystemsExercise';
import M02IdentitiesExercise from './M02IdentitiesExercise';
import M03SimultaneousEquationsExercise from './M03SimultaneousEquationsExercise';
import M04AnglesParallelLinesTrianglesQuadrilateralsExercise from './M04AnglesParallelLinesTrianglesQuadrilateralsExercise';
import M05PercentageExercise from './M05PercentageExercise';
import M06TransformationsExercise from './M06TransformationsExercise';
import M07ExponentialLogarithmicExercise from './M07ExponentialLogarithmicExercise';
import M08QuadraticFunctionsEquationsExercise from './M08QuadraticFunctionsEquationsExercise';
import M09PolynomialsRemainderFactorTheoremExercise from './M09PolynomialsRemainderFactorTheoremExercise';
import M10InequalitiesLinearProgrammingExercise from './M10InequalitiesLinearProgrammingExercise';
import M11RatiosExercise from './M11RatiosExercise';
import M12ExerciseSet from './MathsTopics/M12 Exercise Set.jsx';
import M13CirclesExercise from './M13CirclesExercise';
import M14MensurationExercise from './M14MensurationExercise';
import M15TrigonometryExercise from './M15TrigonometryExercise';
import M19LocusExercise from './M19LocusExercise';
import M21ProbabilityExercise from './M21ProbabilityExercise';
import M22StatisticsExercise from './M22StatisticsExercise';
import M23CrossTopicExercise from './M23CrossTopicExercise';
import MathsCardGenerator from './MathsCardGenerator';

const HKDSEMaths = () => {
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);
  const [activeTab, setActiveTab] = useState('topics');
  const [showExercise, setShowExercise] = useState(false);
  const [currentExerciseModule, setCurrentExerciseModule] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [showMathsCard, setShowMathsCard] = useState(false);

  // Initialize MathJax when component mounts
  useEffect(() => {
    if (window.MathJax) {
      window.MathJax.typesetPromise();
    }
  }, [selectedTopic, activeTab]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Safety check for data - moved after all hooks
  if (!hkdseMathsData) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">數據載入中...</h2>
          <p className="text-gray-600">正在載入HKDSE數學數據，請稍候...</p>
        </div>
      </div>
    );
  }

  const handleTopicSelect = (topic) => {
    setSelectedTopic(topic);
    setExpandedSection(null);
    setIsDropdownOpen(false);
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'basic':
        return 'bg-green-100 text-green-800';
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-800';
      case 'advanced':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getDifficultyText = (difficulty) => {
    switch (difficulty) {
      case 'basic':
        return '基礎';
      case 'intermediate':
        return '中級';
      case 'advanced':
        return '高級';
      default:
        return '未知';
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-800 mb-4">{hkdseMathsData.title}</h1>
        <p className="text-xl text-gray-600">{hkdseMathsData.description}</p>
      </div>

      {/* Navigation Tabs */}
      <div className="flex flex-wrap justify-center mb-8 bg-white rounded-lg shadow-md p-2">
        {[
          { id: 'topics', label: '課題概覽', icon: BookOpen },
          { id: 'pastpapers', label: 'Past Paper', icon: Clock },
          { id: 'assessment', label: 'Assessment', icon: Target },
          { id: 'geogebra', label: 'Geogebra', icon: Calculator },
          { id: 'practice', label: '練習測驗', icon: CheckCircle },
          { id: 'mathscard', label: 'AI 學習卡片', icon: Brain }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === tab.id
                ? 'bg-blue-500 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <tab.icon className="w-5 h-5" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Topics Overview */}
      {activeTab === 'topics' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Topics Dropdown */}
          <div className="lg:col-span-1">
            <h2 className="text-2xl font-bold mb-4">數學課題</h2>
            
            {/* Dropdown Combobox */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full flex items-center justify-between p-4 bg-white border border-gray-300 rounded-lg shadow-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              >
                <div className="flex items-center space-x-3">
                  {selectedTopic ? (
                    <>
                      <div className="flex items-center space-x-2">
                        <span className="font-semibold text-lg">{selectedTopic.code}</span>
                        {selectedTopic.hasExerciseSystem && (
                          <div className="w-2 h-2 bg-green-500 rounded-full" title="有練習系統"></div>
                        )}
                      </div>
                      <span className="text-gray-600">-</span>
                      <span className="text-gray-800">{selectedTopic.title}</span>
                    </>
                  ) : (
                    <span className="text-gray-500">選擇一個課題...</span>
                  )}
                </div>
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
                  {hkdseMathsData.topics?.map((topic) => (
                    <div
                      key={topic.id}
                      onClick={() => handleTopicSelect(topic)}
                      className={`p-4 cursor-pointer transition-colors hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                        selectedTopic?.id === topic.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <h3 className="font-semibold text-lg">{topic.code}</h3>
                            {topic.hasExerciseSystem && (
                              <div className="w-2 h-2 bg-green-500 rounded-full" title="有練習系統"></div>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 mb-2">{topic.title}</p>
                          <div className="flex items-center space-x-2">
                            <div className={`px-2 py-1 rounded text-xs ${getDifficultyColor(topic.difficulty)}`}>
                              {getDifficultyText(topic.difficulty)}
                            </div>
                            {topic.hasExerciseSystem && (
                              <div className="text-xs text-green-600 flex items-center space-x-1">
                                <CheckCircle className="w-3 h-3" />
                                <span>練習可用</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Topic Details */}
          <div className="lg:col-span-2">
            {selectedTopic ? (
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="mb-6">
                  <h2 className="text-3xl font-bold text-gray-800 mb-2">
                    {selectedTopic.code}: {selectedTopic.title}
                  </h2>
                  <p className="text-gray-600 mb-4">{selectedTopic.description}</p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>{selectedTopic.estimatedTime}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Award className="w-4 h-4" />
                        <span>{getDifficultyText(selectedTopic.difficulty)}</span>
                      </div>
                    </div>
                    
                    {/* Exercise Button for Available Modules */}
                    {selectedTopic.hasExerciseSystem && (
                      <button
                        onClick={() => {
                          setCurrentExerciseModule(selectedTopic.code);
                          setShowExercise(true);
                        }}
                        className="flex items-center space-x-2 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors"
                      >
                        <Play className="w-4 h-4" />
                        <span>開始練習</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Key Points */}
                <div className="mb-6">
                  <button
                    onClick={() => toggleSection('keyPoints')}
                    className="flex items-center justify-between w-full text-left p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <h3 className="text-lg font-semibold">重點內容</h3>
                    {expandedSection === 'keyPoints' ? <ChevronUp /> : <ChevronDown />}
                  </button>
                  
                  {expandedSection === 'keyPoints' && (
                    <div className="mt-3 p-4 bg-blue-50 rounded-lg">
                      <ul className="space-y-2">
                        {selectedTopic.keyPoints?.map((point, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Exercises */}
                <div className="mb-6">
                  <button
                    onClick={() => toggleSection('exercises')}
                    className="flex items-center justify-between w-full text-left p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <h3 className="text-lg font-semibold">練習題目</h3>
                    {expandedSection === 'exercises' ? <ChevronUp /> : <ChevronDown />}
                  </button>
                  
                  {expandedSection === 'exercises' && (
                    <div className="mt-3 space-y-4">
                      {selectedTopic.exercises?.map((exercise, index) => (
                        <div key={index} className="p-4 bg-green-50 rounded-lg">
                          <h4 className="font-semibold mb-2">{exercise.title}</h4>
                          <p className="text-gray-700 mb-3">{exercise.question}</p>
                          {exercise.items && (
                            <div className="space-y-2">
                              {exercise.items?.map((item, itemIndex) => (
                                <div key={itemIndex} className="flex items-center justify-between p-2 bg-white rounded">
                                  <span className="math-content">{item.number || item.expression}</span>
                                  <span className="text-sm text-gray-600">{item.answer}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Past Papers */}
                <div className="mb-6">
                  <button
                    onClick={() => toggleSection('pastPapers')}
                    className="flex items-center justify-between w-full text-left p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <h3 className="text-lg font-semibold">歷屆試題</h3>
                    {expandedSection === 'pastPapers' ? <ChevronUp /> : <ChevronDown />}
                  </button>
                  
                  {expandedSection === 'pastPapers' && (
                    <div className="mt-3 space-y-4">
                      {selectedTopic.pastPapers?.map((paper, index) => (
                        <div key={index} className="p-4 bg-yellow-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-semibold">{paper.year}年 第{paper.questionNumber}題</h4>
                            <span className="text-sm bg-yellow-200 px-2 py-1 rounded">
                              {paper.marks}分
                            </span>
                          </div>
                          <p className="text-gray-700 mb-3">{paper.question}</p>
                          <div className="p-3 bg-white rounded border-l-4 border-yellow-400">
                            <p className="text-sm font-medium text-gray-600 mb-1">解答:</p>
                            <p className="math-content">{paper.solution}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">選擇一個課題</h3>
                <p className="text-gray-500">點擊上方的下拉選單來選擇課題</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Past Papers */}
      {activeTab === 'pastpapers' && (
        <PastPaperViewer />
      )}

      {/* Assessment */}
      {activeTab === 'assessment' && (
        <Assessment />
      )}

      {/* Geogebra Graph Tool */}
      {activeTab === 'geogebra' && (
        <GeogebraGraph />
      )}

      {/* Practice Tests */}
      {activeTab === 'practice' && (
        <div className="space-y-6">
          {hkdseMathsData.practiceTests?.map((test) => (
            <div key={test.id} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <CheckCircle className="w-6 h-6 text-green-500 mr-3" />
                  <h3 className="text-lg font-semibold">{test.title}</h3>
                </div>
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4" />
                    <span>{test.timeLimit}分鐘</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Target className="w-4 h-4" />
                    <span>{test.questions.length}題</span>
                  </div>
                </div>
              </div>
              
              <p className="text-gray-700 mb-4">{test.description}</p>
              
              <div className="space-y-4">
                {test.questions?.map((question, index) => (
                  <div key={question.id} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold">第{index + 1}題</h4>
                      <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                        {question.topic}
                      </span>
                    </div>
                    <p className="text-gray-700 mb-3">{question.question}</p>
                    
                    {question.type === 'multiple-choice' && question.options && (
                      <div className="space-y-2 mb-3">
                        {question.options?.map((option) => (
                          <div key={option.id} className="flex items-center space-x-2">
                            <span className="w-6 h-6 bg-white border border-gray-300 rounded-full flex items-center justify-center text-sm">
                              {option.id.toUpperCase()}
                            </span>
                            <span>{option.text}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className="p-3 bg-white rounded border-l-4 border-green-400">
                      <p className="text-sm font-medium text-gray-600 mb-1">解答:</p>
                      <p className="math-content">{question.explanation}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* AI Maths Card Generator */}
      {activeTab === 'mathscard' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Brain className="w-6 h-6 text-purple-500 mr-3" />
                <h3 className="text-lg font-semibold">AI 數學學習卡片生成器</h3>
              </div>
              <div className="flex items-center space-x-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                <Zap className="w-4 h-4" />
                <span>DeepSeek AI</span>
              </div>
            </div>
            
            <p className="text-gray-700 mb-4">
              使用 DeepSeek AI 技術生成精美的數學學習卡片，包含視覺化圖形、詳細解題步驟和 PDF 導出功能。
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="flex items-center space-x-3 p-3 bg-purple-50 rounded-lg">
                <BookOpen className="w-5 h-5 text-purple-500" />
                <div>
                  <p className="font-semibold text-purple-800">智能生成</p>
                  <p className="text-sm text-purple-600">AI 自動生成學習內容</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                <Download className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="font-semibold text-blue-800">PDF 導出</p>
                  <p className="text-sm text-blue-600">一鍵導出為 PDF 文件</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <div>
                  <p className="font-semibold text-green-800">視覺化學習</p>
                  <p className="text-sm text-green-600">多個 SVG 圖形展示</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => setShowMathsCard(true)}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-300 shadow-lg"
              >
                <Brain className="w-5 h-5 mr-2 inline" />
                開始生成學習卡片
              </button>
              <button className="px-4 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors">
                查看示例
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Exercise System Modal/Overlay */}
      {showExercise && (
        <div className="fixed inset-0 z-50 bg-white overflow-y-auto">
          <div className="relative">
            <div className="sticky top-0 bg-white/95 backdrop-blur-sm p-4 z-20 border-b border-gray-200">
              <button
                onClick={() => {
                  setShowExercise(false);
                  setCurrentExerciseModule(null);
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors shadow-lg"
              >
                <span>← 返回課題概覽</span>
              </button>
            </div>
            
            {/* Exercise content area */}
            <div className="relative">
              {/* Render appropriate exercise component based on module */}
          {currentExerciseModule === 'M01' && (
            <M01NumberSystemsExercise 
              onProgress={(progress) => {
                console.log('M01 Exercise progress:', progress);
              }}
            />
          )}
          {currentExerciseModule === 'M02' && (
            <M02IdentitiesExercise 
              onProgress={(progress) => {
                console.log('M02 Exercise progress:', progress);
              }}
            />
          )}
          {currentExerciseModule === 'M03' && (
            <M03SimultaneousEquationsExercise 
              onProgress={(progress) => {
                console.log('M03 Exercise progress:', progress);
              }}
            />
          )}
          {currentExerciseModule === 'M04' && (
            <M04AnglesParallelLinesTrianglesQuadrilateralsExercise 
              onProgress={(progress) => {
                console.log('M04 Exercise progress:', progress);
              }}
            />
          )}
          {currentExerciseModule === 'M05' && (
            <M05PercentageExercise 
              onProgress={(progress) => {
                console.log('M05 Exercise progress:', progress);
              }}
            />
          )}
          {currentExerciseModule === 'M06' && (
            <M06TransformationsExercise 
              onProgress={(progress) => {
                console.log('M06 Exercise progress:', progress);
              }}
            />
          )}
          {currentExerciseModule === 'M07' && (
            <M07ExponentialLogarithmicExercise 
              onProgress={(progress) => {
                console.log('M07 Exercise progress:', progress);
              }}
            />
          )}
          {currentExerciseModule === 'M08' && (
            <M08QuadraticFunctionsEquationsExercise 
              onProgress={(progress) => {
                console.log('M08 Exercise progress:', progress);
              }}
            />
          )}
          {currentExerciseModule === 'M09' && (
            <M09PolynomialsRemainderFactorTheoremExercise 
              onProgress={(progress) => {
                console.log('M09 Exercise progress:', progress);
              }}
            />
          )}
          {currentExerciseModule === 'M10' && (
            <M10InequalitiesLinearProgrammingExercise 
              onProgress={(progress) => {
                console.log('M10 Exercise progress:', progress);
              }}
            />
          )}
          {currentExerciseModule === 'M11' && (
            <M11RatiosExercise 
              onProgress={(progress) => {
                console.log('M11 Exercise progress:', progress);
              }}
            />
          )}
          {currentExerciseModule === 'M12' && (
            <M12ExerciseSet 
              onProgress={(progress) => {
                console.log('M12 Exercise progress:', progress);
              }}
            />
          )}
          {currentExerciseModule === 'M13' && (
            <M13CirclesExercise 
              onProgress={(progress) => {
                console.log('M13 Exercise progress:', progress);
              }}
            />
          )}
          {currentExerciseModule === 'M14' && (
            <M14MensurationExercise 
              onProgress={(progress) => {
                console.log('M14 Exercise progress:', progress);
              }}
            />
          )}
          {currentExerciseModule === 'M15' && (
            <M15TrigonometryExercise 
              onProgress={(progress) => {
                console.log('M15 Exercise progress:', progress);
              }}
            />
          )}
          {currentExerciseModule === 'M19' && (
            <M19LocusExercise 
              onProgress={(progress) => {
                console.log('M19 Exercise progress:', progress);
              }}
            />
          )}
          {currentExerciseModule === 'M21' && (
            <M21ProbabilityExercise 
              onProgress={(progress) => {
                console.log('M21 Exercise progress:', progress);
              }}
            />
          )}
          {currentExerciseModule === 'M22' && (
            <M22StatisticsExercise 
              onProgress={(progress) => {
                console.log('M22 Exercise progress:', progress);
              }}
            />
          )}
          {currentExerciseModule === 'M23' && (
            <M23CrossTopicExercise 
              onProgress={(progress) => {
                console.log('M23 Exercise progress:', progress);
              }}
            />
          )}
            </div>
          </div>
        </div>
      )}

      {/* MathsCard Generator Modal/Overlay */}
      {showMathsCard && (
        <div className="fixed inset-0 z-50 bg-white overflow-y-auto">
          <div className="relative">
            <div className="sticky top-0 bg-white/95 backdrop-blur-sm p-4 z-20 border-b border-gray-200">
              <button
                onClick={() => setShowMathsCard(false)}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors shadow-lg"
              >
                <span>← 返回 HKDSE 數學</span>
              </button>
            </div>
            
            {/* MathsCard content area */}
            <div className="relative">
              <MathsCardGenerator />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HKDSEMaths; 